import { Box, Container, Stack, Text, VStack } from '@chakra-ui/react'

import Image from 'next/image'
import { InlineWidget } from 'react-calendly'
import { ReviewProps } from '../HeaderAndVideo/HeaderVideo'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import gsap from 'gsap'
import useIsomorphicLayoutEffect from '@/hooks/useIsomorphicLayoutEffect'
import useSelector from '@/hooks/useSelector'

gsap.registerPlugin(ScrollTrigger)

const Contact = (props: ReviewProps) => {
  const [q, mainRef] = useSelector<HTMLDivElement>(null)

  useIsomorphicLayoutEffect(() => {
    ScrollTrigger.matchMedia({
      // desktop
      '(min-width: 48em)': function () {
        const textAnimation = gsap.fromTo(
          q('#review-booking-text'),
          {
            y: 400,
            opacity: 0,
            delay: 2,
          },
          {
            y: 0,
            duration: 1.5,
            opacity: 1,

            scrollTrigger: {
              id: 'parallax',
              trigger: q('#review-booking-text'),
              start: () => '-=400 bottom',
              end: '-=300 bottom',
              markers: false,
              toggleActions: 'pause play none none',
            },
          }
        )

        const contactAnimatiion = gsap.fromTo(
          q('#review-booking-text2'),
          {
            x: -200,
            delay: 2,
            opacity: 0,
          },
          {
            opacity: 1,
            x: 0,
            duration: 2.2,
            scrollTrigger: {
              id: 'parallax',
              trigger: q('#review-booking-text'),
              start: () => '-=200 bottom',
              end: '-=100 bottom',
              markers: false,
              toggleActions: 'pause play none none',
            },
          }
        )

        const calendlyAnimation = gsap.fromTo(
          q('#review-booking-calendar'),
          {
            x: 500,
            delay: 2,
            opacity: 0,
          },
          {
            x: 100,
            duration: 2.2,
            opacity: 1,
            scrollTrigger: {
              id: 'parallax',
              trigger: q('#review-booking-text'),
              start: () => '-=100 bottom',
              end: '-=100 bottom',
              markers: false,
              toggleActions: 'pause play none none',
            },
          }
        )

        return () => {
          textAnimation.scrollTrigger?.kill()
          textAnimation.kill()

          contactAnimatiion.scrollTrigger?.kill()
          contactAnimatiion.kill()

          calendlyAnimation.scrollTrigger?.kill()
          calendlyAnimation.kill()
        }
      },
    })
  }, [])

  return (
    <Box ref={mainRef}>
      <Container
        centerContent
        minH="50vh"
        maxW="container.xl"
        w="container.xl"
        pt={{ base: 40, md: 20 }}
      >
        <VStack spacing={{ base: 0, md: 60 }} pb={{ base: 40, md: 0 }}>
          <VStack
            id="review-booking-text"
            pb={{ base: 0, md: 10 }}
            fontSize="4xl"
            px="50"
          >
            <Text>Book a free 60 Minute Consultation (Usually $250)</Text>
            <Text>
              and learn how you can fix these problems on your website.
            </Text>
          </VStack>
        </VStack>

        <Stack
          overflow="hidden"
          direction={{ base: 'column', md: 'row' }}
          w="full"
        >
          <Stack
            id="review-booking-text2"
            direction={{ base: 'row', md: 'column' }}
            spacing={{ base: 10, md: 0 }}
            alignItems="left"
            px={{ base: 50, md: 0 }}
            mt={{ base: 0, md: '66px' }}
          >
            <Box
              mb={10}
              alignSelf="left"
              w={{ base: '350px', md: '250px' }}
              h={{ base: '350px', md: '250px' }}
              position="relative"
              rounded="3xl"
              boxShadow="xl"
            >
              <Image
                priority
                objectFit="contain"
                layout="fill"
                src={
                  props.review?.chacraRepPhoto?.publicUrl
                    ? props.review?.chacraRepPhoto.publicUrl
                    : 'https://res.cloudinary.com/hfq3eckim/image/upload/v1650618658/csv2/Icons/istockphoto-1220827245-170667a_kgvkfs.png'
                }
                alt="contact-person"
              />
            </Box>
            <Stack
              fontSize={{ base: '4xl', md: '3xl' }}
              alignSelf="center"
              spacing={35}
            >
              <VStack
                display={{ base: 'none', md: 'contents' }}
                spacing={0}
                alignItems="center"
                textAlign="left"
              >
                <Text textAlign="left">Book a time for your</Text>
                <Text textAlign="left">complimentary consultation</Text>
              </VStack>

              <Text display={{ base: 'contents', md: 'none' }} textAlign="left">
                Book a time for your complimentary consultation
              </Text>

              <VStack spacing={0} alignItems="left" textAlign="left">
                <Text>Alternatively give us a call on</Text>
                <Text textAlign="left">
                  <strong>{props.review?.chacraRepPhoneNo}</strong>
                </Text>
              </VStack>
            </Stack>
          </Stack>

          <Box
            display={{ base: 'none', md: 'flex' }}
            alignSelf={{ base: 'center', md: 'top' }}
            id="review-booking-calendar"
          >
            <InlineWidget
              styles={{
                height: '1100px',
                width: '650px',
                marginTop: 0,
              }}
              url={props.review?.chacraRepCalendlyURL ?? ''}
              prefill={{
                name: props.review?.leadName ? props.review?.leadName : '',
                email: props.review?.email ? props.review?.email : '',
                location: 'UAE',
                customAnswers: {
                  a1: props.review?.phoneNumber
                    ? props.review?.phoneNumber
                    : '',
                },
              }}
            />
          </Box>

          <Box
            display={{ base: 'flex', md: 'none' }}
            alignSelf={{ base: 'center', md: 'none' }}
            id="review-booking-calendar"
          >
            <InlineWidget
              styles={{
                height: '1100px',
                width: '1100px',
                marginTop: 0,
              }}
              url={props.review?.chacraRepCalendlyURL ?? ''}
              prefill={{
                name: props.review?.leadName ? props.review?.leadName : '',
                email: props.review?.email ? props.review?.email : '',
                location: 'UAE',
                customAnswers: {
                  a1: props.review?.phoneNumber
                    ? props.review?.phoneNumber
                    : '',
                },
              }}
            />
          </Box>
        </Stack>
      </Container>
    </Box>
  )
}

export default Contact
